import {
  bulkDeleteEntitiesResponseSchema,
  bulkSubmitForReviewResponseSchema,
  changeRequestResponseSchema,
  changeRequestSchema,
  type AdminBulkDeleteEntityType,
  type BulkDeleteEntitiesParams,
  type BulkDeleteEntitiesResponse,
  type BulkDeleteEntityType,
  type BulkSubmitForReviewParams,
  type BulkSubmitForReviewResponse,
  type ChangeRequest,
  type ChangeRequestEntityType,
  type ListResult,
  type PaginationParams,
} from '../model';
import { http, unwrapData } from '@noor/api';
import { getList, postOne, type RequestOptions } from './http';

// Moderation Service — the MERCHANT-facing third of it.
//
// The gate itself is described in the app's CLAUDE.md (#14): most merchant
// writes are queued rather than applied, answering with a `pending_change`
// receipt. These three RPCs are how a merchant drives that queue directly —
// hand an entity in, take it back, and see where everything stands.
//
// The admin RPCs of the same service (the global queue, approve, reject) are
// deliberately not wrapped: they belong to the back-office panel.
//
// Scope: every route here is merchant-scoped through the JWT claim, so none of
// them takes a `merchant_id` — the same rule as the rest of the surface.

/**
 * Filters for `GET /merchant/change-requests`.
 *
 * `status` and `entity_type` are bare strings on the wire. `status` is typed
 * loosely because the screen's filter passes one of the five documented
 * values, while `entity_type` reuses the closed union — a filter for an entity
 * kind the gate does not cover would narrow to nothing.
 */
export type ListChangeRequestsParams = PaginationParams & {
  status?: string;
  entity_type?: ChangeRequestEntityType;
  restaurant_id?: string;
};

export const moderationApi = {
  /**
   * The caller's own change requests — `GET /merchant/change-requests`.
   *
   * Merchant-scoped by the token, so this is the only listing a merchant panel
   * can read; the admin's global queue is a different route.
   */
  list(
    params?: ListChangeRequestsParams,
    options?: RequestOptions,
  ): Promise<ListResult<ChangeRequest>> {
    return getList('merchant/change-requests', changeRequestSchema, params, options);
  },

  /**
   * Hand a finished entity to moderation —
   * `POST /merchant/entities/{entity_type}/{entity_id}/submit-for-review`, empty body.
   *
   * ONE route for every moderated entity, keyed by `entity_type` in the path:
   * from the panel it is the same «готово» press whatever is being submitted.
   * It moves the entity from `created` to `ready_for_life` and lands a
   * `transition` request in the admin queue — approving it publishes the
   * entity, rejecting it sends the entity back to `created` for rework.
   *
   * Answers with the created request, which the caller needs: cancelling later
   * addresses the REQUEST by id, not the entity.
   *
   * `restaurantApi.submitForReview` is the branch-specific predecessor of this
   * route and still exists; this one supersedes it for every entity type.
   */
  async submitForReview(
    entityType: ChangeRequestEntityType,
    entityId: string,
  ): Promise<ChangeRequest | undefined> {
    const { change_request } = await postOne(
      `merchant/entities/${entityType}/${entityId}/submit-for-review`,
      {},
      changeRequestResponseSchema,
    );
    return change_request;
  },

  /**
   * Submit drafts of an entity type to moderation —
   * `POST /merchant/entities/{entity_type}/submit-for-review`.
   *
   * Request body carries `{ created_before?: string; entity_ids?: string[] }`
   * (or empty `{}`). `entity_ids` narrows the call to the listed entities;
   * omitting it (or passing an empty array) submits every eligible draft.
   * Answers with `{ results, submitted_count, failed_count, fiscal_codes_missing, remaining }`.
   */
  async submitAllForReview(
    entityType: ChangeRequestEntityType,
    params?: BulkSubmitForReviewParams,
  ): Promise<BulkSubmitForReviewResponse> {
    const body: Record<string, unknown> = {};
    if (params?.created_before) body.created_before = params.created_before;
    if (params?.entity_ids && params.entity_ids.length > 0) {
      body.entity_ids = params.entity_ids;
    }
    const raw = await http.post<unknown>(
      `merchant/entities/${entityType}/submit-for-review`,
      body,
    );
    return bulkSubmitForReviewResponseSchema.parse(unwrapData(raw));
  },

  /**
   * Withdraw a still-pending request — `POST /merchant/change-requests/{id}/cancel`,

   * empty body. The request comes back as `withdrawn` and the entity returns
   * to `created`, editable again.
   *
   * Addressed by the REQUEST's id rather than the entity's, which is why
   * {@link submitForReview} returns the record it created.
   */
  async cancel(changeRequestId: string): Promise<ChangeRequest | undefined> {
    const { change_request } = await postOne(
      `merchant/change-requests/${changeRequestId}/cancel`,
      {},
      changeRequestResponseSchema,
    );
    return change_request;
  },

  /**
   * Bulk delete entities of a given entity type —
   * `POST /api/v1/merchant/entities/{entity_type}/bulk-delete`.
   *
   * Supported entity types: restaurant, product, product_category, promotion,
   * modifier_group, modifier_option.
   * `ids` must contain 1-100 UUIDs; duplicates are collapsed.
   * `restaurant_id` is optional and needed only for modifier_group/modifier_option.
   */
  async bulkDelete(
    entityType: BulkDeleteEntityType,
    params: BulkDeleteEntitiesParams,
  ): Promise<BulkDeleteEntitiesResponse> {
    const body: Record<string, unknown> = {
      ids: Array.from(new Set(params.ids)),
    };
    if (params.restaurant_id) {
      body.restaurant_id = params.restaurant_id;
    }
    const raw = await http.post<unknown>(
      `merchant/entities/${entityType}/bulk-delete`,
      body,
    );
    return bulkDeleteEntitiesResponseSchema.parse(unwrapData(raw));
  },

  /**
   * Admin-facing bulk delete over a wider set of entity types —
   * `POST /api/v1/admin/entities/{entity_type}/bulk-delete`.
   */
  async bulkDeleteAdmin(
    entityType: AdminBulkDeleteEntityType,
    ids: string[],
  ): Promise<BulkDeleteEntitiesResponse> {
    const body = {
      ids: Array.from(new Set(ids)),
    };
    const raw = await http.post<unknown>(
      `admin/entities/${entityType}/bulk-delete`,
      body,
    );
    return bulkDeleteEntitiesResponseSchema.parse(unwrapData(raw));
  },
};
